/*eslint-disable*/
import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

export default function ModelUsageRechart({ data = [], xLabels = [], height = 350 }) {
  // Determine length (use max runs length)
  const maxLen = Math.max(0, ...data.map(s => (Array.isArray(s.runs) ? s.runs.length : 0)));

  // Build xLabels if not provided
  const labels = Array.isArray(xLabels) && xLabels.length === maxLen
    ? xLabels
    : Array.from({ length: maxLen }, (_, i) => `P${i + 1}`);

  // Transform series-array into recharts-friendly array of objects:
  const seriesKeys = data.map(s => s.model);

  const chartData = useMemo(() => {
    return labels.map((lab, idx) => {
      const obj = { label: lab };
      data.forEach(s => {
        // We use 0 to get the "dip to zero" behavior
        obj[s.model] = Number.isFinite(s.runs?.[idx]) ? s.runs[idx] : 0;
      });
      return obj;
    });
  }, [data, labels]);

  // Color generator
  const colors = seriesKeys.map((_, i) => {
    const hue = Math.round((i * 360) / Math.max(1, seriesKeys.length));
    return `hsl(${hue} 80% 45%)`;
  });

  // Calculate dynamic padding based on number of models
  // Each item is ~26px (16px icon + 10px gap), plus 24px for box padding
  const legendHeight = Math.min(180, 24 + (seriesKeys.length * 26));
  const dynamicPadding = legendHeight + 20; // Add 20px extra space

  // early return for no-data
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="no-data-message">
        <p>No AI processing data available for this period.</p>
      </div>
    );
  }

  // --- This function will now ONLY render the LineChart ---
  const renderChart = () => {
    return (
      <LineChart data={chartData} margin={{ top: 5, right: 24, left: 12, bottom: 50 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} angle={0} dy={8} padding={{ left: 30, right: 30 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip
          wrapperStyle={{ borderRadius: 8 }}
          contentStyle={{ borderRadius: 8 }}
          formatter={(value, name) => [`${value} runs`, name]}
        />
        {seriesKeys.map((key, idx) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={colors[idx]}
            strokeWidth={2.5}
            dot={{ r: 5 }} // Slightly bigger dots
            activeDot={{ r: 8 }}
            connectNulls
            isAnimationActive={false} // Disable animation for stability
          />
        ))}
      </LineChart>
    );
  };

  return (
    <div style={{ width: "100%", minHeight: height, position: 'relative', paddingTop: `${dynamicPadding}px` }}>
      {/* Legend Box - Top Right */}
      <div style={{
        position: 'absolute',
        top: '0',
        right: '0',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '12px 16px',
        backgroundColor: 'white',
        borderRadius: '10px',
        border: '2px solid #e0e0e0',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        zIndex: 10,
        maxWidth: '200px',
        maxHeight: '180px',
        overflowY: 'auto'
      }}>
        {seriesKeys.map((key, idx) => (
          <div key={key} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: colors[idx],
              borderRadius: '3px',
              flexShrink: 0
            }} />
            <span style={{ color: '#2c3e50' }}>{key}</span>
          </div>
        ))}
      </div>

      {/* Line Chart */}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}